import { Discount, DiscountOrder, Price, Tax } from './restaurant.model';
import {Address, FulfillmentType} from './order.model';

export interface ShopLineItemMap {
  [key: string]: {
    amount: number;
    line_items: LineItem[];
    discounts: any[];
    metadata?: Record<string, string>;
  };
}

export interface ShopLineItem {
  amount: number;
  line_items: LineItem[];
  discounts: Discount[];
  metadata?: Record<string, string>;
}

export enum Mode {
  mobile = 'kiosk',
  desktop = 'desktop',
  mobileApp = 'mobile'
}

export enum NonceType {
  APPLE_PAY = 'APPLE_PAY',
  GOOGLE_PAY = 'GOOGLE_PAY',
  GIFT_CARD = 'GIFT_CARD',
  REGULAR = 'REGULAR',
  SUBSCRIPTION = 'SUBSCRIPTION',
  APPLE_PAY_NATIVE = "APPLE_PAY_NATIVE",
  GOOGLE_PAY_NATIVE = "GOOGLE_PAY_NATIVE",
  MEMBERSHIP = 'MEMBERSHIP'
}

export enum LoyaltyBannerType {
  CHECKOUT = 'checkout',
  CART = 'cart',
  CUSTOMER = 'customer'
}

export enum SquareStatus {
  ok = 'OK',
  cancel = 'Cancel',
  abort = 'Abort',
  error = 'Error',
  invalid = 'Invalid',
  unknown = 'Unknown'
}
export enum SquareCardBrand {
  american_express = 'AMERICAN_EXPRESS',
  diners_club_card = 'DISCOVER_DINERS',
  discover_card = 'DISCOVER',
  jcb = 'JCB',
  mastercard = 'MASTERCARD',
  visa = 'VISA',
  union_pay_card = 'CHINA_UNIONPAY',
  other = 'OTHER_BRAND'
}

export enum SquareCardType {
  credit = 'CREDIT',
  debit = 'DEBIT',
  unknown = 'UNKNOWN_CARD_TYPE'
}
export enum SquarePrepaidType {
  not_prepaid = 'NOT_PREPAID',
  prepaid = 'PREPAID',
  unknown = 'UNKNOWN_PREPAID_TYPE'
}
export enum SquareMethodType {
  ach = 'ACH',
  apple_pay = 'Apple Pay',
  card = 'Card',
  gift_card = 'Gift Card',
  google_pay = 'Google Pay'
}

export class Loyalty {
  constructor(
    public pointsName: any = null,
    public pointsValue: number = 0,
    public pointsEnabled: boolean = true
  ) {
  }
}

export class CatalogObject {
  constructor(
    public catalog_object_id: string,
    public category_id?: string,
    public name?: string,
    public base_price_money?: Price,
    public onByDefault: boolean = false,
    public hiddenFromCustomer: boolean = false,
    public isSelected: boolean = false,
    public quantity: string = '1'
  ) {
  }
}

export class TaxObject {
  constructor(
    public tax_uid: string
  ) {
  }
}

export class LineItem {
  constructor(
    public quantity: number,
    public catalog_object_id?: string,
    public category_id?: string,
    public variations?: CatalogObject[],
    public modifiers?: CatalogObject[] | null,
    public note?: string,
    public name?: string,
    public variationName?: string,
    public base_price_money?: Price,
    public metadata?: any,
    public item_type?: string
  ) {
  }
}

export class Order {
  constructor(
    public createOrderRequest: ShopLineItem,
    public square_customer_id: string,
    public platform_user_id: string,
    public platform_channel: string,
    public order_bot_user_id: string,
    public platform_merchant_id: string,
    public tip_money: number,
    public pickup_at: any,
    public pickupLocation: string,
    public print_ahead_duration: string,
    public pickup_window_duration: string,
    public given_name: string,
    public family_name: string,
    public table_number: string = null,
    public fulfillment_type: FulfillmentType = null,
    public is_tab_order = false,
    public is_group_order = false,
    public email: string,
    public billing_address: BillingAddress = null,
    public delivery_time: string = null,
    public delivery_quote_id: string = null,
    public delivery_address: Address = new Address(),
    public save_delivery_address: boolean = false,
    public is_asap: boolean = false,
    public use_loyalty_points: boolean = false,
    public applied_taxes: string[] = null,
    public platform_name: string = 'web',
    public is_requesting_sms_link: boolean = false,
    public proposed_rewards: any[] = [],
    public square_loyalty_account_id: string = null,
    public delivery_gratuity_cents: number = 0,
    public proposed_discount_codes: DiscountOrder[] = [],
    public is_additional_tab_round: boolean = false,
    public estimated_prep_time_duration: string = null,
    public custom_number: string = null,
    public is_shared_order: boolean = false,
    public shared_order_id: string = null,
    public single_order_ids: string[] = [],
    public ikon_pass_id: string = null,
    public is_subscription_order: boolean = false,
    public gift_card_order_id: string = null,
    public is_customer_balance_account_order: boolean = false,
    public buyer_email: string = null,
  ) {
  }
}

export class OrderTab {
  constructor(
    public tab_id: string = null,
    public transaction_id: string = null,
    public customer_id: string = null,
    public square_orders: any[] = [],
    public merchant_id: string = null,
    public location_id: string = null,
    public kyoo_fee: number = 0.25,
    public is_closed: boolean = false,
    public transaction_order_id: string = null,
    public auth_amount_in_cents: number = 0,
    public subtotal_amount: number = 0,
    public total_discount_amount: number = 0,
    public total_tax_amount: number = 0,
    public total_amount: number = 0
  ) {
  }
}

export class TabInStorage {
  constructor(
    public tabId: string,
    public locationId: string,
    public isGroup: boolean
  ) {
  }
}

export class GroupOrder {
  constructor(
    public platform_name: string = null,
    public platform_user_id: string = null,
    public platform_channel: string = null,
    public order_bot_user_id: string = null,
    public square_customer_id: string = null,
    public createOrderRequest: GroupOrderRequest[] = [],
    public fulfillment_type: string = null,
    public is_asap: boolean = true,
    public pickup_at: string = null,
    public table_number: string = null,
    public is_tab_order: boolean = false,
    public tip_money: number = 0,
    public given_name: string = null,
    public family_name: string = null,
    public billing_address: BillingAddress = null,
    public email: string = null,
    public delivery_time: string = null,
    public delivery_quote_id: string = null,
    public delivery_address: Address = null,
    public save_delivery_address: boolean = false,
    public delivery_gratuity_cents: number = 0,
    public custom_number: string = null
  ) {
  }
}

export class GroupOrderTab {
  constructor(
    public group_order_id: string = null,
    public group_id: string = null,
    public customer_id: string = null,
    public rounds: TabRound[] = [],
    public is_closed: boolean = false,
    public subtotal_amount: number = 0,
    public total_discount_amount: number = 0,
    public total_tax_amount: number = 0,
    public total_amount: number = 0
  ) {
  }
}

export class TabRound {
  constructor(
    public round_number: number,
    public round_orders: RoundOrder[]
  ) {
  }
}

export class RoundOrder {
  constructor(
    public location_id: string,
    public line_items: LineItem[]
  ) {
  }
}

export class GroupOrderRequest {
  constructor(
    public location_id: string,
    public order_id: string,
    public order: ShopLineItem
  ) {
  }
}

export interface OrderInfo {
  id: string;
  order: Order;
  amount: number;
  loyaltyPoints: number;
  discounts: Discount[];
  taxes: Tax[];
  isUpdated: boolean;
  isSubmitted: boolean;
  isPaid: boolean;
  estimatedDuration?: string;
  estimatedDurationDefault?: string;
  kyoo_fee_money?: number;
  delivery_fee_money?: number;
  total_tax_money?: number;
}

export interface OrderMap {
  [key: string]: OrderInfo;
}

export interface BillingAddress {
  address_line1: string;
  address_line2: string;
  address_line3: string;
  locality: string;
  country: string;
  organization: string;
  postal_code: string;
  sublocality: string;
  sublocality1: string;
  sublocality2: string;
  administrative_district_level1: string;
  administrative_district_level2: string;
  administrative_district_level3: string;
}

export class SquareTokenize {
  constructor(
    public details: SquareDetails = new SquareDetails(),
    public errors: SquareTokenError[] = [],
    public status: SquareStatus = null,
    public token: string = null
  ) {
  }
}

export class SquareDetails {
  constructor(
    public bankAccount: string = null,
    public card: SquareCard = new SquareCard,
    public giftCard: SquareGiftCard = new SquareGiftCard,
    public method: SquareMethodType = null,
    public shipping: SquareShippingDetails = new SquareShippingDetails,
    public billing: SquareShippingContact = new SquareShippingContact
  ) {
  }
}

export class SquareGiftCard {
  constructor(
    public brand: string = null,
    public expMonth: number = null,
    public expYear: number = null,
    public last4: string = null
  ) {
  }
}
export class SquareCard {
  constructor(
    public billing: string = null,
    public brand: SquareCardBrand = null,
    public cardType: SquareCardType = null,
    public expMonth: number = null,
    public expYear: number = null,
    public last4: string = null,
    public prepaidType: SquarePrepaidType = null,

  ) {
  }
}

export class SquareShippingDetails {
  constructor(
    public contact: SquareShippingContact = new SquareShippingContact,
    public option: SquareShippingOption = new SquareShippingOption
  ) {
  }
}
export class SquareShippingContact {
  constructor(
    public addressLines: string[] = null,
    public city: string | undefined = null,
    public countryCode: string | undefined = null,
    public email: string | undefined = null,
    public familyName: string | undefined = null,
    public givenName: string | undefined = null,
    public phone: string | undefined = null,
    public postalCode: string | undefined = null,
    public state: string | undefined = null
  ) {
  }
}
export class SquareShippingOption {
  constructor(
    public amount: string = null,
    public id: string = null,
    public label: string = null
  ) {
  }
}
export class SquareTokenError {
  constructor(
    public field: string = null,
    public message: string = null,
    public type: string = null
  ) {
  }
}
export class PaymentSources {
  constructor(
    public tenders: PaymentTender[] = []
  ) {
  }
}

export class PaymentTender {
  constructor(
    public source_id: string = null,
    public is_gift_card: boolean = false
  ) {
  }
}

export class SavedPaymentState {
  constructor(
    public cardId: string = null,
    public isSavedPayment: boolean = false,
    public touched: boolean = true
  ) {
  }
}

export class KioskSmsOrder {
  constructor(
    public orderId: string,
    public fulfillmentType: string,
    public lineItems: LineItem[],
    public isAsap: boolean,
    public pickupAt: Date,
    public tableNumber: string,
    public squareOrder: any
  ) {
  }
}

export class SavedOrder {
  constructor(
    public isCreated: boolean = false,
    public locationId: string = null,
    public merchantId: string = null
  ) {
  }
}
